import React, { useCallback, useEffect, useMemo } from "react";
import { useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { FileUploader } from "react-drag-drop-files";
import ImageUploadProgressTile from "./ImageUploadProgressTile";
import { Icon } from "@vds/icons";
import { Button } from "@vds/buttons";
import { Modal, ModalTitle, ModalBody, ModalFooter } from "@vds/modals";
import { ErrorMessage, Field, FieldArray, useFormikContext } from "formik";
import { useLocation } from "react-router-dom";
import FloorPlanTileEdit from "./FloorPlanTileEdit";
import { deleteMedia, imageUploadMedia } from "../../model/mediaService";
import { useSelector } from "react-redux";
import { handleSiteLevelTagsDelete } from "../Sites/SiteService";
import styles from "./components/basicForm.module.css";
import { Loader } from "@vds/loaders";
import { useDispatch } from "react-redux";
import { device } from "../../styles/breakpoints";
import {
  deleteFloorImageMapping,
  saveFloorImageMapping,
  showNotificationBanner,
} from "../../store/actions";
import { processImageToGrayscaleBlob, deleteFloorPlan, handleDeleteAreaSubArea } from "./sitesUtil";

const TextForFloorPlan = styled.div`
  /* Mobile-first: consistent readable text */
  color: #000;
  font-feature-settings: "clig" off, "liga" off;
  font-family: "Verizon-NHG-eDS";
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0.5px;

  @media ${device.tablet} {
    font-size: 16px;
    line-height: 20px;
  }
`;
const FrameForDragAndDrop = styled.div`
  /* Mobile-first: full width matching text above */
  display: flex;
  width: 100%;
  min-height: 152px;
  padding: 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
  border: 1px dashed #000;
  background: var(--white, #fff);

  @media ${device.tablet} {
    padding: 20px;
    gap: 9px;
  }

  @media ${device.desktop} {
    padding: 24px;
  }
`;
const InnerFrameForDragAndDrop = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  width: auto;
  height: auto;
`;
const FrameForImageDesc = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  width: auto;
  height: auto;
`;
const ImageDescription = styled.div`
  /* Mobile-first: smaller, readable text */
  color: #000;
  text-align: center;
  font-feature-settings: "clig" off, "liga" off;
  font-family: "Verizon-NHG-eDS";
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.5px;

  @media ${device.tablet} {
    font-size: 14px;
    line-height: 18px;
  }

  @media ${device.desktop} {
    font-size: 16px;
    line-height: 20px;
  }
`;

const TilesContainer = styled.div`
  /* Mobile-first: conditional scroll based on content */
  max-height: ${props => props.$hasMultiple ? '340px' : 'none'};
  width: 100%;
  margin-top: 10px;
  overflow-y: ${props => props.$hasMultiple ? 'auto' : 'visible'};
  overflow-x: hidden;

  /* Show scrollbar only when needed */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const FrameForFileUploader = styled.div`
  /* Mobile-first: column layout, then row on tablet+ */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;

  @media ${device.tablet} {
    flex-direction: row;
    gap: 9px;
  }
`;

const FrameForUploadIcon = styled.div`
  /* Mobile-first: hide icon on mobile to save space, show on tablet+ */
  display: none;

  @media ${device.tablet} {
    display: flex;
    width: 44px;
    height: 44px;
    border-radius: 99px;
    border: 1px solid rgba(111, 113, 113, 0.06);
    background: rgba(111, 113, 113, 0.06);
    justify-content: center;
    align-items: center;
  }
`;

const FileUploaderWrapper = styled.div`
  /* Ensure FileUploader component takes full width */
  width: 100%;

  /* Override any default FileUploader styling */
  & > * {
    width: 100% !important;
  }
`;

const UploadText = styled.div`
  /* Mobile-first: smaller text, scales up */
  color: #000;
  text-align: center;
  font-feature-settings: "clig" off, "liga" off;
  font-family: "Verizon-NHG-eDS";
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;

  @media ${device.tablet} {
    font-size: 18px;
    line-height: 22px;
  }

  @media ${device.desktop} {
    font-size: 20px;
    line-height: 24px;
  }
`;

const FrameForSBEdit = styled.div`
  /* Mobile-first: conditional scroll based on content */
  width: 100%;
  height: ${props => props.$hasMultiple ? '70%' : 'auto'};
  display: flex;
  overflow-x: ${props => props.$hasMultiple ? 'auto' : 'visible'};
  overflow-y: hidden;
  gap: 12px;
  -webkit-overflow-scrolling: touch;

  /* Custom scrollbar for better UX */
  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }

  /* Original tablet/desktop styles (unchanged from before) */
  @media ${device.tablet} {
    gap: 16px;
  }

  @media ${device.desktop} {
    max-width: 756px;
    gap: 20px;
  }

  /* Child divs should fill available width when single tile */
  & > div {
    width: ${props => props.$hasMultiple ? 'auto' : '100%'};
    flex-shrink: 0;
  }
`;

const maxSize = 10;
const totalByteSize = maxSize * 1024 * 1024;

function FloorPlanAddSite({ isEditable, clearFormSiteTagList, onFloorUpload, onValidationChange }) {
  const dispatch = useDispatch();
  const fileTypes = useMemo(() => ["JPG", "PNG", "JPEG"], []);
  const { setFieldValue, values } = useFormikContext();
  const { activeSession } = useSelector(
    (state) => state.sessionReducer.activeSession
  );
  const [showErrorModal, setShowErrorModal] = useState(false);
  const location = useLocation();
  const mode = location.state?.mode || "add";
  const sizeErrorFunction = useCallback((err) => {
    setShowErrorModal(true);
  }, []);
  const [loading, setLoading] = useState(false);

  const handleFilesChange = async (newFiles, pushFile) => {
    const filesArray = Array.isArray(newFiles)
      ? newFiles
      : Array.from(newFiles);
    if (filesArray.length > 0) {
      if (!filesArray.some((file) => file.size > totalByteSize)) {
        setLoading(true);
        try {
          const uploadedFiles = await Promise.all(
            filesArray.map((file) => {
              return new Promise((resolve) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onloadend = async (e) => {
                  try {
                    const { blob, grayscaleImageUrl } =
                      await processImageToGrayscaleBlob(e.target.result);
                    const arrayBuffer = await blob.arrayBuffer();
                    const response = await imageUploadMedia(
                      arrayBuffer,
                      activeSession
                    );
                    if (response?.status !== 413) {
                      const fileData = {
                        originalFile: file,
                        id: null,
                        imageid: response.id,
                        url: grayscaleImageUrl,
                        name:
                          file.name.indexOf(".") > -1
                            ? file.name.split(".")[0]
                            : file.name,

                        floorName: file.name,
                      };
                      const notificationInfo = {
                        type: "Image upload success",
                        priority: "success",
                        data: {
                          title: "Floor plan image uploaded successfully",
                          subTitle: "",
                        },
                        showNotification: true,
                      };
                      dispatch(showNotificationBanner(notificationInfo));
                      resolve(fileData);
                    } else {
                      resolve(null);
                    }
                  } catch (error) {
                    const notificationInfo = {
                      type: "Image upload fail",
                      priority: "critical",
                      data: {
                        title: "Floor plan failed to upload",
                        subTitle: "Please try again",
                      },
                      showNotification: true,
                    };
                    dispatch(showNotificationBanner(notificationInfo));
                    resolve(null);
                  }
                };
                reader.onerror = () => resolve(null);
              });
            })
          );
          const validFiles = uploadedFiles.filter(
            (fileObj) => fileObj !== null
          );
          validFiles.forEach((fileObj) => {
            pushFile(fileObj);
            if (mode === "edit" && isEditable) {
              onFloorUpload?.();
            }
          });
          if (mode === "add" && validFiles.length) {
            setFieldValue(`sites`, [
              ...(values.sites?.length ? values.sites : []),
              ...validFiles.map((fileObj) => ({
                floorName: fileObj.name,
                areas: [],
                fileId: fileObj.imageid,
              })),
            ]);
          }
        } catch (error) {
          console.error("Error uploading files:", error);
        } finally {
          setLoading(false);
        }
      } else {
        console.error("One or more files exceed the total byte size limit.");
      }
    } else {
      console.error("No files selected.");
    }
  };

  const handleFileChange = useCallback(
    async (newFile, index) => {
      if (!!newFile) {
        if (newFile.size < totalByteSize) {
          try {
            const deleteImageId =
              values.files[index].image || values.files[index].imageid;
            const uploadedFile = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.readAsDataURL(newFile);
              reader.onloadend = async (e) => {
                try {
                  const { blob, grayscaleImageUrl } =
                    await processImageToGrayscaleBlob(e.target.result);
                  const arrayBuffer = await blob.arrayBuffer();
                  const response = await imageUploadMedia(
                    arrayBuffer,
                    activeSession
                  );
                  if (response) {
                    const fileData = {
                      originalFile: newFile,
                      imageid: response.id,
                      url: grayscaleImageUrl,
                      name: newFile.name,
                    };
                    dispatch(
                      saveFloorImageMapping(
                        values.files[index].id,
                        grayscaleImageUrl
                      )
                    );
                    resolve(fileData);
                  } else {
                    resolve(null);
                  }
                } catch (error) {
                  resolve(null);
                }
              };
              reader.onerror = () => resolve(null);
            });
            if (uploadedFile !== null) {
              setFieldValue(`files[${index}]`, {
                ...values.files[index],
                ...uploadedFile,
                floorName: uploadedFile.name,
              });
              setFieldValue(`sites[${index}]`, {
                ...values.sites[index],
                floorName: uploadedFile.name,
                fileId: uploadedFile.id,
              });
              await deleteMedia(deleteImageId, activeSession);
            }
          } catch (error) {
            console.error("Error deleting image:", error);
          }
        } else {
          console.error("One or more files exceed the total byte size limit.");
        }
      } else {
        console.error("No files selected.");
      }
    },
    [values]
  );

  return (
    <>
      {loading && (
        <div className="loader-absolute-container">
          <Loader active fullscreen={false} surface="light" />
        </div>
      )}
      {showErrorModal && (
        <Modal
          opened={showErrorModal}
          surface="light"
          fullScreenDialog={false}
          disableAnimation={false}
          disableOutsideClick={false}
          ariaLabel="Testing Modal"
        >
          <ModalTitle>File type/size error</ModalTitle>
          <ModalBody>
            Max file size: 10 MB <br />
            Supported file types: JPG, PNG, JPEG
          </ModalBody>
          <ModalFooter>
            <Button
              style={{ display: "block", margin: "0 auto" }}
              size="large"
              disabled={false}
              use="primary"
              data-testid="floorPlanAddSite_closeButton"
              onClick={() => setShowErrorModal(false)}
            >
              Close
            </Button>
          </ModalFooter>
        </Modal>
      )}
      {mode === "add" && (
        <TextForFloorPlan>
          Upload any floor plans that you would like to appear in the 5G Video
          Insights portal. You may upload as many floor plans as necessary for
          representing all camera locations across your facility.
          <br />
          <br />
          Note: All floor plans will be converted to black and white upon
          upload.
        </TextForFloorPlan>
      )}
      <FieldArray name="files">
        {({ push: pushFile, remove: removeFile }) => (
          <>
            {(mode === "add" || (mode === "edit" && isEditable)) && (
              <FileUploaderWrapper>
                <FileUploader
                  multiple={true}
                  handleChange={(newFiles) =>
                    handleFilesChange(newFiles, pushFile)
                  }
                  name="files"
                  onSizeError={sizeErrorFunction}
                  types={fileTypes}
                  maxSize={maxSize}
                  fileOrFiles={values.files}
                >
                  <FrameForDragAndDrop>
                    <InnerFrameForDragAndDrop>
                      <FrameForFileUploader>
                        <FrameForUploadIcon>
                          <Icon name="upload" size="large" color="#000000" />
                        </FrameForUploadIcon>
                        <UploadText>Drag files to upload or </UploadText>
                        <Button surface="light" use="secondary">
                          Browse
                        </Button>
                      </FrameForFileUploader>
                      <FrameForImageDesc>
                        <ImageDescription>Max file size: 10 MB</ImageDescription>
                        <ImageDescription>
                          Supported file types: JPG, PNG, JPEG
                        </ImageDescription>
                      </FrameForImageDesc>
                    </InnerFrameForDragAndDrop>
                  </FrameForDragAndDrop>
                </FileUploader>
              </FileUploaderWrapper>
            )}
            <>
              {mode === "add" ? (
                <TilesContainer $hasMultiple={values.files?.length > 1}>
                  {values.files?.length ? (
                    values.files.map((fileObj, index) => (
                      <ImageUploadProgressTile
                        key={index}
                        file={fileObj.originalFile}
                        index={index}
                        removeFile={removeFile}
                        sizeErrorFunction={sizeErrorFunction}
                        fileTypes={fileTypes}
                        maxSize={maxSize}
                        handleFileChange={handleFileChange}
                        onValidationChange={onValidationChange}
                      />
                    ))
                  ) : (
                    <ErrorMessage
                      name={`missingFiles`}
                      component={"div"}
                      className={styles.errorLabel}
                      style={{ marginLeft: "10px" }}
                    />
                  )}
                </TilesContainer>
              ) : (
                <FrameForSBEdit $hasMultiple={values.files?.length > 1}>
                  {values.files?.length
                    ? values.files.map((fileObj, index) => (
                        <div key={index}>
                          <FloorPlanTileEdit
                            file={fileObj}
                            index={index}
                            isEditable={isEditable}
                            mode={mode}
                            handleDelete={(id) => {
                              if(id === null && index === values.files.length - 1){
                                removeFile(index);
                                dispatch(showNotificationBanner({
                                  type: "Floor delete success",
                                  priority: "success",
                                  data: {
                                    title: "Floor plan deleted successfully",
                                    subTitle: "",
                                  },
                                  showNotification: true,
                                }));
                                return;
                              }
                              deleteFloorPlan({
                                mode,
                                id,
                                index,
                                values,
                                activeSession,
                                deleteMediaFn: deleteMedia,
                                handleSiteDeleteFn: handleSiteLevelTagsDelete,
                                dispatch,
                                deleteFloorImageMappingAction: deleteFloorImageMapping,
                                cb: () => {
                                  removeFile(index);
                                  id && clearFormSiteTagList(id, "floors");
                                 handleDeleteAreaSubArea(index, values, clearFormSiteTagList, setFieldValue);
                                  setFieldValue(
                                    "sites",
                                    values.sites.filter((f, fI) => fI !== index)
                                  );
                                  dispatch(showNotificationBanner({
                                    type: "Floor delete success",
                                    priority: "success",
                                    data: {
                                      title: "Floor plan deleted successfully",
                                      subTitle: "",
                                    },
                                    showNotification: true,
                                  }));
                                },
                              })
                            }
                            }
                            sizeErrorFunction={sizeErrorFunction}
                            fileTypes={fileTypes}
                            maxSize={maxSize}
                            handleFileChange={handleFileChange}
                            onValidationChange={onValidationChange}
                          />
                        </div>
                      ))
                    : null}
                </FrameForSBEdit>
              )}
            </>
          </>
        )}
      </FieldArray>
    </>
  );
}

export default FloorPlanAddSite;
